import React from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { SubscribePlanButton } from "../../../components/Billing/BillingActions";

/**
 * Stripe Checkout cancel_return_url → `/billing/canceled`
 * Same shell as BillingSuccess: blurred admin preview (Admin layout) + centered card + notice.
 */
export default function BillingCanceled() {
  const history = useHistory();

  const goToDashboard = () => {
    history.push("/dashboard");
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const noticeBg = useColorModeValue("cyan.50", "rgba(56, 187, 209, 0.15)");
  const noticeBorder = useColorModeValue("cyan.200", "cyan.600");

  return (
    <Box position="fixed" inset={0} zIndex={1000} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box position="absolute" inset={0} bg="blackAlpha.400" aria-hidden />
      <Box
        position="relative"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-canceled-heading"
        maxW="lg"
        w="full"
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="xl"
        p={{ base: 8, md: 10 }}
        textAlign="center"
      >
        <Box as="p" fontSize="sm" fontWeight="600" color="orange.500" letterSpacing="0.06em" textTransform="uppercase" mb={3}>
          Checkout canceled
        </Box>
        <Box
          as="h1"
          id="billing-canceled-heading"
          fontSize="2xl"
          fontFamily="'Urbanist', sans-serif"
          color={headingColor}
          fontWeight="600"
          mb={4}
          lineHeight="short"
        >
          No payment was completed
        </Box>

        <Box
          borderWidth="1px"
          borderColor={noticeBorder}
          bg={noticeBg}
          borderRadius="md"
          p={4}
          textAlign="left"
          mb={6}
        >
          <Box as="p" fontWeight="semibold" fontSize="sm" mb={1} color={headingColor}>
            Checkout canceled
          </Box>
          <Box as="p" fontSize="sm" color={muted}>
            You can subscribe or start a trial whenever you&apos;re ready. Nothing was charged.
          </Box>
        </Box>

        <Box as="p" color={muted} fontSize="sm" lineHeight="tall" mb={6}>
          When you&apos;re ready, restart checkout below.
        </Box>

        <Box display="flex" flexDirection="column" gap={3} alignItems="center">
          <SubscribePlanButton plan="standard" interval="month" preferSubscribeCopy size="lg" />
          <SubscribePlanButton plan="plus" interval="month" preferSubscribeCopy size="lg" />
        </Box>

        <Box mt={4} fontSize="sm" color={muted}>
          Prefer yearly or the 3-month offer?{" "}
          <Box
            as="button"
            type="button"
            onClick={() => history.push("/paywall")}
            color="cyan.500"
            fontWeight="500"
            textDecoration="underline"
            bg="transparent"
            border="none"
            cursor="pointer"
            p={0}
            fontSize="inherit"
            _hover={{ color: "cyan.600" }}
          >
            Open the plan picker
          </Box>
        </Box>

        <Box mt={6} fontSize="xs" color={muted} lineHeight="tall">
          The main dashboard stays subscription-gated until you have a plan.{" "}
          <Box
            as="button"
            type="button"
            onClick={goToDashboard}
            color="cyan.500"
            fontWeight="500"
            textDecoration="underline"
            bg="transparent"
            border="none"
            cursor="pointer"
            p={0}
            fontSize="inherit"
            _hover={{ color: "cyan.600" }}
          >
            Open dashboard anyway
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
